import { useMemo } from "react";

import { useUser } from "@ui/hooks/useUser";

import { useLoyaltyAchievements, useReferralStats } from "@jti/rest-api";

import type { SimplifiedMilestone } from "../../LoyaltyReferralStats.types";

export type UseUserReferralData = () => {
  isLoading: boolean;
  isAllAchievementsDone: boolean;
  totalUserReferrals: number;
  referredFriendsCountForStatistics: number;
  friendsReferralsUntilNextAchievement: number;
  milestones: SimplifiedMilestone[];
  pointsEarned: number;
  isComponentHidden?: boolean;
};

const TARGET_EVENTS = [
  "referrer_network_first_purchase",
  "referrer_network_purchase",
  "referrers_affiliate_registered",
];

export const useUserReferralData: UseUserReferralData = () => {
  const { user } = useUser();
  const {
    isLoading: isLoyaltyStatisticLoading,
    data: loyaltyStatisticsData,
    error: loyaltyStatisticsError,
  } = useReferralStats({
    uuid: user?.uuid ?? "",
    ciam_token: user?.ciam_token ?? "",
  });
  const {
    data: achievements,
    isLoading: isAchievementsLoading,
    error: achievementsError,
  } = useLoyaltyAchievements({
    uuid: user?.uuid ?? "",
    ciam_token: user?.ciam_token ?? "",
  });

  const { friendsReferralsUntilNextAchievement, isAllAchievementsDone, milestones, totalUserReferrals } =
    useMemo(() => {
      const relevantAchievements = achievements?.items
        ?.filter(
          ({ rules }) => (rules?.length ?? 0) !== 0 && rules?.[0] && TARGET_EVENTS.includes(rules[0].custom_event)
        )
        .sort((cur, next) => {
          return (cur.rules?.[0]?.goal ?? 0) - (next.rules?.[0]?.goal ?? 0);
        });

      if (!relevantAchievements || relevantAchievements.length <= 0) {
        return {
          friendsReferralsUntilNextAchievement: 0,
          totalUserReferrals: 0,
          isAllAchievementsDone: false,
          milestones: [],
        };
      }

      const currentAchievementIndex = relevantAchievements.findIndex(({ completed_count }) => !completed_count);

      const isAllAchievementsDone = currentAchievementIndex === -1;

      const currentAchievement =
        relevantAchievements[isAllAchievementsDone ? relevantAchievements.length - 1 : currentAchievementIndex];

      const currentGoal = currentAchievement.rules?.[0]?.goal ?? 0;
      const currentValue = currentAchievement.rules?.[0]?.value ?? 0;

      const friendsReferralsUntilNextAchievement = currentGoal - currentValue;

      const milestones = relevantAchievements.map(({ rules }) => ({
        referralNumber: rules?.[0]?.goal ?? 0,
        mileStonePointsValue: rules?.[0]?.points_value ?? 0,
      }));

      const maxReferralNumber = Math.max(...milestones.map((milestone) => milestone.referralNumber ?? 0));

      return {
        milestones,
        isAllAchievementsDone,
        friendsReferralsUntilNextAchievement,
        totalUserReferrals: isAllAchievementsDone ? maxReferralNumber : currentValue,
      };
    }, [achievements]);

  return {
    milestones,
    isAllAchievementsDone,
    friendsReferralsUntilNextAchievement,
    totalUserReferrals,
    referredFriendsCountForStatistics: loyaltyStatisticsData?.completed_first_order_count ?? 0,
    pointsEarned: loyaltyStatisticsData?.earned_points ?? 0,
    isLoading: isLoyaltyStatisticLoading || isAchievementsLoading,
    isComponentHidden: !achievements || !loyaltyStatisticsData || !!loyaltyStatisticsError || !!achievementsError,
  };
};
